import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import { Plus, Pencil, Trash2, LogOut } from "lucide-react";
import { ImageDropzone } from "../components/admin/ImageDropzone";

const MENU_EMPTY = { name: "", price: "", description: "", image: "", order: 0 };
const GALLERY_EMPTY = { image: "", caption: "", span: "normal", order: 0 };

function ItemForm({ fields, initial, onSubmit, testid }) {
  const [form, setForm] = useState(initial);
  const change = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <form
      data-testid={testid}
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(form);
      }}
      className="space-y-4"
    >
      {fields.map((f) => (
        <div key={f.key}>
          <Label className="label-caps text-ink/60">{f.label}</Label>
          {f.key === "image" ? (
            <div className="mt-2">
              <ImageDropzone
                value={form.image}
                onChange={(url) => change("image", url)}
                testid={`${testid}-image`}
              />
            </div>
          ) : f.textarea ? (
            <Textarea
              value={form[f.key]}
              onChange={(e) => change(f.key, e.target.value)}
              className="mt-2"
              data-testid={`form-${f.key}`}
            />
          ) : (
            <Input
              type={f.type || "text"}
              value={form[f.key]}
              onChange={(e) =>
                change(f.key, f.type === "number" ? Number(e.target.value) : e.target.value)
              }
              className="mt-2"
              data-testid={`form-${f.key}`}
            />
          )}
        </div>
      ))}
      <DialogFooter>
        <Button type="submit" className="bg-charcoal text-cream rounded-full" data-testid="form-save">
          Save
        </Button>
      </DialogFooter>
    </form>
  );
}

export default function AdminDashboard() {
  const { user, ready, logout } = useAuth();
  const navigate = useNavigate();
  const [menu, setMenu] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [dialog, setDialog] = useState(null); // {type, mode, item}

  useEffect(() => {
    if (ready && !user) navigate("/admin/login");
  }, [ready, user, navigate]);

  const load = useCallback(async () => {
    try {
      const [m, g, e] = await Promise.all([
        api.get("/menu"),
        api.get("/gallery"),
        api.get("/enquiries"),
      ]);
      setMenu(m.data);
      setGallery(g.data);
      setEnquiries(e.data);
    } catch {
      /* handled by interceptor */
    }
  }, []);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  const save = async (type, mode, item, id) => {
    try {
      if (mode === "create") await api.post(`/${type}`, item);
      else await api.put(`/${type}/${id}`, item);
      toast.success("Saved");
      setDialog(null);
      load();
    } catch {
      toast.error("Save failed");
    }
  };

  const remove = async (type, id) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      await api.delete(`/${type}/${id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const menuFields = [
    { key: "name", label: "Name" },
    { key: "price", label: "Price (e.g. NZ$19)" },
    { key: "description", label: "Description", textarea: true },
    { key: "image", label: "Image URL" },
    { key: "order", label: "Order", type: "number" },
  ];
  const galleryFields = [
    { key: "image", label: "Image URL" },
    { key: "caption", label: "Caption" },
    { key: "span", label: "Span (normal / tall / wide)" },
    { key: "order", label: "Order", type: "number" },
  ];

  if (!user) return null;

  return (
    <div className="min-h-screen bg-surface">
      <header className="bg-ink text-cream">
        <div className="mx-auto max-w-6xl px-6 py-5 flex items-center justify-between">
          <div className="flex items-baseline gap-2">
            <span className="font-serif text-2xl">OKOME</span>
            <span className="label-caps text-gold">Admin</span>
          </div>
          <Button
            variant="ghost"
            onClick={() => {
              logout();
              navigate("/admin/login");
            }}
            data-testid="admin-logout"
            className="text-cream hover:text-gold gap-2"
          >
            <LogOut size={16} /> Logout
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-10">
        <Tabs defaultValue="menu">
          <TabsList data-testid="admin-tabs">
            <TabsTrigger value="menu" data-testid="tab-menu">Menu ({menu.length})</TabsTrigger>
            <TabsTrigger value="gallery" data-testid="tab-gallery">Gallery ({gallery.length})</TabsTrigger>
            <TabsTrigger value="enquiries" data-testid="tab-enquiries">Enquiries ({enquiries.length})</TabsTrigger>
          </TabsList>

          {/* MENU */}
          <TabsContent value="menu" className="mt-6">
            <div className="flex justify-end mb-4">
              <Dialog open={dialog?.type === "menu"} onOpenChange={(o) => !o && setDialog(null)}>
                <DialogTrigger asChild>
                  <Button
                    onClick={() => setDialog({ type: "menu", mode: "create", item: MENU_EMPTY })}
                    data-testid="add-menu-btn"
                    className="bg-charcoal text-cream rounded-full gap-2"
                  >
                    <Plus size={16} /> Add Dish
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{dialog?.mode === "edit" ? "Edit" : "Add"} Dish</DialogTitle>
                  </DialogHeader>
                  {dialog?.type === "menu" && (
                    <ItemForm
                      testid="menu-form"
                      fields={menuFields}
                      initial={dialog.item}
                      onSubmit={(form) => save("menu", dialog.mode, form, dialog.item.id)}
                    />
                  )}
                </DialogContent>
              </Dialog>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {menu.map((it) => (
                <div key={it.id} className="bg-cream p-4 flex gap-4 items-center" data-testid={`admin-menu-${it.id}`}>
                  <img src={it.image} alt={it.name} className="h-16 w-16 object-cover" />
                  <div className="flex-1">
                    <p className="font-serif text-lg">{it.name}</p>
                    <p className="text-sm text-ink/60">{it.price}</p>
                  </div>
                  <button onClick={() => setDialog({ type: "menu", mode: "edit", item: it })} className="p-2 hover:text-gold" data-testid={`edit-menu-${it.id}`}>
                    <Pencil size={16} />
                  </button>
                  <button onClick={() => remove("menu", it.id)} className="p-2 hover:text-destructive" data-testid={`delete-menu-${it.id}`}>
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* GALLERY */}
          <TabsContent value="gallery" className="mt-6">
            <div className="flex justify-end mb-4">
              <Dialog open={dialog?.type === "gallery"} onOpenChange={(o) => !o && setDialog(null)}>
                <DialogTrigger asChild>
                  <Button
                    onClick={() => setDialog({ type: "gallery", mode: "create", item: GALLERY_EMPTY })}
                    data-testid="add-gallery-btn"
                    className="bg-charcoal text-cream rounded-full gap-2"
                  >
                    <Plus size={16} /> Add Image
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{dialog?.mode === "edit" ? "Edit" : "Add"} Image</DialogTitle>
                  </DialogHeader>
                  {dialog?.type === "gallery" && (
                    <ItemForm
                      testid="gallery-form"
                      fields={galleryFields}
                      initial={dialog.item}
                      onSubmit={(form) => save("gallery", dialog.mode, form, dialog.item.id)}
                    />
                  )}
                </DialogContent>
              </Dialog>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {gallery.map((it) => (
                <div key={it.id} className="bg-cream p-2 group relative" data-testid={`admin-gallery-${it.id}`}>
                  <img src={it.image} alt={it.caption} className="w-full h-32 object-cover" />
                  <p className="text-xs mt-2 text-ink/60 truncate">{it.caption}</p>
                  <div className="absolute top-3 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => setDialog({ type: "gallery", mode: "edit", item: it })} className="bg-cream/90 p-1.5" data-testid={`edit-gallery-${it.id}`}>
                      <Pencil size={14} />
                    </button>
                    <button onClick={() => remove("gallery", it.id)} className="bg-cream/90 p-1.5 hover:text-destructive" data-testid={`delete-gallery-${it.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* ENQUIRIES */}
          <TabsContent value="enquiries" className="mt-6">
            <div className="space-y-3">
              {enquiries.length === 0 && <p className="text-ink/50">No enquiries yet.</p>}
              {enquiries.map((e) => (
                <div key={e.id} className="bg-cream p-5 relative group" data-testid={`admin-enquiry-${e.id}`}>
                  <button
                    onClick={() => remove("enquiries", e.id)}
                    data-testid={`delete-enquiry-${e.id}`}
                    aria-label="Delete enquiry"
                    className="absolute top-3 right-3 p-2 text-ink/40 hover:text-destructive opacity-60 group-hover:opacity-100 transition-opacity"
                  >
                    <Trash2 size={16} />
                  </button>
                  <div className="flex justify-between flex-wrap gap-2 pr-8">
                    <p className="font-serif text-lg">{e.name} {e.company && `· ${e.company}`}</p>
                    <span className="text-xs text-ink/40">{new Date(e.created_at).toLocaleString()}</span>
                  </div>
                  <p className="text-sm text-ink/60 mt-1">{e.email} · {e.phone || "no phone"} · {e.guests || "?"} guests · {e.event_date || "date tbc"}</p>
                  {e.message && <p className="mt-2 text-ink/80">{e.message}</p>}
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
